import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { money, moneyCompact, num, pct } from "@owners/format";
import { api } from "../api";
import { useHotels } from "./Numbers";
import { LineChart, type Series } from "./LineChart";

/**
 * Trends → Your markets (V018): the hotel's CoStar market and submarket — the
 * cycle (12-month RevPAR with downturns, the hotel's own T12 laid over), where
 * the market is now, the pipeline, values (price per room, cap rate; CoStar's
 * forecast dashed), and Watch / OK signals. Shown only to clients licensed for
 * market data; renders nothing otherwise.
 */
interface Signal { key: string; label: string; watch: boolean; detail: string; rule: string }
interface Downturn { peak: string; trough: string; recovered: string | null; depthPct: number; monthsDown: number; monthsToRecover: number | null }
interface CycleNow { latest: string; monthsSinceTrough: number | null; vsPeakPct: number | null; expansionMonths: number | null; medianExpansionMonths: number | null; lateCycle: boolean | null; inDownturn: boolean }
interface Point { date: string; forecast: boolean; revpar12m: number | null; occupancy12m: number | null; adr12m: number | null; pipelineShare: number | null; salePricePerRoom: number | null; capRate: number | null }
interface Now {
  occupancy: number | null; adr: number | null; revpar: number | null; occupancyChg: number | null; adrChg: number | null; revparChg: number | null;
  supplyChg: number | null; demandChg: number | null; inventoryRooms: number | null; underConstructionRooms: number | null; underConstructionBuildings: number | null;
  deliveredRooms12m: number | null; underConstructionYearAgo: number | null; salePricePerRoom: number | null; salePriceChg: number | null; capRate: number | null; salesVolume12m: number | null;
}
interface AreaView { name: string; level: string; empty?: true; latest?: string | null; now?: Now | null; cycle?: { downturns: Downturn[]; now: CycleNow } | null; signals?: Signal[]; chart?: Point[] }
type MarketRes = { enabled: false } | { enabled: true; hotel: { id: string; name: string; code: string | null }; market: AreaView | null; submarket: AreaView | null; own: Array<{ date: string; value: number }> };

const mLabel = (ym: string) => new Date(ym + "-01T12:00:00Z").toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
const chg = (v: number | null, opts: { points?: boolean } = {}) => v === null ? "" : opts.points ? ` (${v >= 0 ? "+" : "−"}${Math.abs(v * 100).toFixed(1)} pts)` : ` (${pct(v, { ratio: true, signed: true })})`;

export function MarketPanel() {
  const hotels = useHotels().data?.hotels ?? [];
  const [hotelId, setHotelId] = useState("");
  useEffect(() => { if (!hotelId && hotels.length) setHotelId(hotels[0]!.id); }, [hotels, hotelId]);
  const q = useQuery({ queryKey: ["trends-market", hotelId], enabled: !!hotelId, staleTime: 600_000, queryFn: () => api<MarketRes>(`/api/trends/market?hotelId=${hotelId}`) });
  const d = q.data;
  if (!hotelId || !d || !d.enabled) return null;   // not licensed (or still loading the first time): show nothing

  const areas = [d.market, d.submarket].filter((a): a is AreaView => !!a);
  return (
    <>
      <h2>Your markets</h2>
      <div className="row" style={{ marginBottom: 12 }}>
        <select value={hotelId} onChange={(e) => setHotelId(e.target.value)}>{hotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}</select>
        <span className="muted" style={{ fontSize: 12 }}>Market data © CoStar Group, shared under our license for owners and investors. Not for redistribution.</span>
      </div>
      {q.isFetching && !q.data ? <div className="card muted">Loading…</div>
        : !areas.length ? <div className="card muted">This hotel isn't linked to a CoStar market yet.</div>
        : <>
            <CycleCard areas={areas} own={d.own} hotel={d.hotel.code ?? d.hotel.name} />
            {areas.map((a) => <AreaCard key={a.name} a={a} />)}
          </>}
    </>
  );
}

/** Market and submarket 12-month RevPAR since 2000 with the market's downturns shaded and the hotel's own T12 RevPAR. */
function CycleCard({ areas, own, hotel }: { areas: AreaView[]; own: Array<{ date: string; value: number }>; hotel: string }) {
  const base = areas.find((a) => a.chart?.length);
  if (!base) return null;
  const x = [...new Set(areas.flatMap((a) => (a.chart ?? []).filter((p) => !p.forecast).map((p) => p.date)))].sort();
  const pick = (a: AreaView) => { const m = new Map((a.chart ?? []).map((p) => [p.date, p.revpar12m])); return x.map((dt) => m.get(dt) ?? null); };
  const ownMap = new Map(own.map((o) => [o.date, o.value]));
  const series: Series[] = [
    ...areas.filter((a) => a.chart?.length).map((a, i) => ({ label: `${a.name} (${a.level})`, color: i === 0 ? "#15263d" : "#5b7db1", points: pick(a) })),
    ...(own.length ? [{ label: `${hotel} T12`, color: "#2f7d4f", points: x.map((dt) => ownMap.get(dt) ?? null) }] : []),
  ];
  const n = base.cycle?.now;
  return (
    <div className="row" style={{ alignItems: "flex-start", gap: 18 }}>
      <div className="card" style={{ flex: 2, minWidth: 420 }}>
        <h3 style={{ marginTop: 0 }}>Market cycle — 12-month RevPAR</h3>
        <LineChart x={x} series={series} format={(v) => money(v, { decimals: 0 })} tickEvery={36} height={240}
          bands={(base.cycle?.downturns ?? []).map((dt) => ({ from: dt.peak, to: dt.trough }))} />
        <p className="muted" style={{ fontSize: 12 }}>Shaded: {base.name} peak to trough (a fall of 5% or more). Source: CoStar, through {base.latest ? mLabel(base.latest) : "—"}.</p>
        {(base.cycle?.downturns ?? []).map((dt) => (
          <div key={dt.peak} style={{ fontSize: 13 }}>{mLabel(dt.peak)} → {mLabel(dt.trough)}: {pct(dt.depthPct, { signed: true })} over {num(dt.monthsDown)} months{dt.monthsToRecover !== null ? `, recovered in ${num(dt.monthsToRecover)}` : ", not yet recovered"}</div>
        ))}
      </div>
      {n && (
        <div className="card" style={{ flex: 1, minWidth: 280 }}>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <h3 style={{ margin: 0 }}>{base.name} now</h3>
            {n.inDownturn ? <span className="pill bad-pill">In a downturn</span> : n.lateCycle === null ? null
              : <span className={`pill ${n.lateCycle ? "warn-pill" : "ok-pill"}`}>{n.lateCycle ? "Late cycle" : "Mid cycle"}</span>}
          </div>
          <p style={{ fontSize: 14 }}>
            {n.inDownturn ? `12-month RevPAR is ${pct(Math.abs(n.vsPeakPct ?? 0))} below the last peak.`
              : <>Expansion running {num(n.expansionMonths)} months; past expansions lasted {num(n.medianExpansionMonths)} months (median).</>}
          </p>
          <p className="muted" style={{ fontSize: 11 }}>Late-cycle check: the current expansion is at least as long as the median past one (proposed rule).</p>
        </div>
      )}
    </div>
  );
}

function AreaCard({ a }: { a: AreaView }) {
  if (a.empty || !a.chart) return <div className="card"><h3 style={{ marginTop: 0 }}>{a.name}</h3><p className="muted">No CoStar data uploaded yet.</p></div>;
  const n = a.now;
  const tail = a.chart.filter((p) => p.date >= "2015-01");
  const x = tail.map((p) => p.date);
  const lastActual = tail.reduce((k, p, i) => (p.forecast ? k : i), -1);
  const split = (get: (p: Point) => number | null): Series[] => [
    { label: "Actual", color: "#15263d", points: tail.map((p, i) => (i <= lastActual ? get(p) : null)) },
    ...(lastActual < tail.length - 1 ? [{ label: "CoStar forecast", color: "#15263d", dashed: true, points: tail.map((p, i) => (i >= lastActual ? get(p) : null)) }] : []),
  ];
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{a.name} <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>· {a.level} · through {a.latest ? mLabel(a.latest) : "—"}</span></h3>
      {n && (
        <div className="tiles">
          <div className="card tile"><div className="eyebrow">Occupancy (12 mo)</div><div className="tile-value" style={{ fontSize: 22 }}>{pct(n.occupancy, { ratio: true })}</div><div className="tile-sub">{chg(n.occupancyChg, { points: true })} vs a year ago</div></div>
          <div className="card tile"><div className="eyebrow">ADR (12 mo)</div><div className="tile-value" style={{ fontSize: 22 }}>{money(n.adr, { decimals: 2 })}</div><div className="tile-sub">{chg(n.adrChg)} vs a year ago</div></div>
          <div className="card tile"><div className="eyebrow">RevPAR (12 mo)</div><div className="tile-value" style={{ fontSize: 22 }}>{money(n.revpar, { decimals: 2 })}</div><div className="tile-sub">{chg(n.revparChg)} vs a year ago</div></div>
          <div className="card tile"><div className="eyebrow">Supply / demand</div><div className="tile-value" style={{ fontSize: 18 }}>{pct(n.supplyChg, { ratio: true, signed: true })} / {pct(n.demandChg, { ratio: true, signed: true })}</div><div className="tile-sub">12-month growth</div></div>
          <div className="card tile"><div className="eyebrow">Pipeline</div><div className="tile-value" style={{ fontSize: 22 }}>{num(n.underConstructionRooms)} rooms</div>
            <div className="tile-sub">{n.underConstructionRooms !== null && n.inventoryRooms ? `${pct(n.underConstructionRooms / n.inventoryRooms, { ratio: true })} of ${num(n.inventoryRooms)} rooms` : ""}{n.underConstructionYearAgo !== null ? ` · ${num(n.underConstructionYearAgo)} a year ago` : ""}</div>
            {n.deliveredRooms12m !== null && <div className="tile-sub">{num(n.deliveredRooms12m)} delivered in 12 mo</div>}</div>
          <div className="card tile"><div className="eyebrow">Value</div><div className="tile-value" style={{ fontSize: 22 }}>{moneyCompact(n.salePricePerRoom)}/room</div>
            <div className="tile-sub">{chg(n.salePriceChg)} vs a year ago · cap rate {pct(n.capRate, { ratio: true, decimals: 2 })}</div></div>
        </div>
      )}
      {!!a.signals?.length && (
        <div className="tiles" style={{ marginTop: 10 }}>
          {a.signals.map((s) => (
            <div key={s.key} className="card tile" title={s.rule}>
              <div className="row" style={{ justifyContent: "space-between" }}><div className="eyebrow">{s.label}</div><span className={`pill ${s.watch ? "warn-pill" : "ok-pill"}`}>{s.watch ? "Watch" : "OK"}</span></div>
              <div className="tile-sub">{s.detail}</div>
            </div>
          ))}
        </div>
      )}
      <div className="row" style={{ alignItems: "flex-start", gap: 18, marginTop: 10 }}>
        <div style={{ flex: 1, minWidth: 300 }}>
          <div className="eyebrow">Price per room</div>
          <LineChart x={x} series={split((p) => p.salePricePerRoom)} format={(v) => moneyCompact(v)} height={180} tickEvery={24} />
        </div>
        <div style={{ flex: 1, minWidth: 300 }}>
          <div className="eyebrow">Cap rate</div>
          <LineChart x={x} series={split((p) => (p.capRate === null ? null : p.capRate * 100))} format={(v) => `${v.toFixed(1)}%`} height={180} tickEvery={24} />
        </div>
        <div style={{ flex: 1, minWidth: 300 }}>
          <div className="eyebrow">Under construction, % of inventory</div>
          <LineChart x={x} series={[{ label: "Pipeline", color: "#8a5a00", points: tail.map((p) => (p.forecast || p.pipelineShare === null ? null : p.pipelineShare * 100)) }]} format={(v) => `${v.toFixed(1)}%`} height={180} tickEvery={24} />
        </div>
      </div>
      <p className="muted" style={{ fontSize: 11 }}>Signal thresholds are proposed; hover a tile for its rule. Dashed: CoStar's forecast.</p>
    </div>
  );
}
